// src/pages/publicacoes/Profissionais.tsx
// UPDATED: 2026-04-10 — v2.0
// ✅ PRF- adicionado ao ID_REGEX
// ✅ NUM_REGEX exige exatamente 6 dígitos (registro FEPCSSP)
// ✅ Prefixo "FEPCSSP-" é removido antes de enviar ao backend
// ✅ Placeholder e dicas atualizados com PRF-

import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight, Search, ShieldCheck, BadgeCheck,
  X, MapPin, ChevronDown, Loader2, Building2,
  Award, CheckCircle, ExternalLink, Hash, CreditCard, User,Calendar, 
} from "lucide-react";
import { API_BASE } from '../utils/apiConfig'


const SITE_URL = (import.meta as any).env?.VITE_APP_URL ?? "https://www.federacaodoscabeleireiros.com.br";


// ─── Valida se a query é um ID ou número FEPCSSP ─────────────────────────────
// Aceita: ALU-xxxx, FIL-xxxx, PRF-xxxx, FEPCSSP-xxxxxx, ou exatamente 6 dígitos
const ID_REGEX = /^(ALU|FIL|PRF|FEPCSSP)[-\s]/i;
const NUM_REGEX = /^\d{6}$/;

const isQueryValida = (q: string): boolean =>
  ID_REGEX.test(q.trim()) || NUM_REGEX.test(q.trim());

/** Remove prefixo "FEPCSSP-" para enviar apenas o número ao backend */
const sanitizeQuery = (q: string): string => {
  const trimmed = q.trim();
  const fepcsspPrefix = trimmed.match(/^FEPCSSP[-\s](.+)$/i);
  if (fepcsspPrefix) return fepcsspPrefix[1].trim();
  return trimmed;
};


// ─── Types ────────────────────────────────────────────────────────────────────
type TipoResultado = "profissional" | "escola";
type TipoVinculo   = "Revalidado" | "Certificado" | "Responsável Técnico";

type ResultadoBusca = {
  id:              string;
  tipo:            TipoResultado;
  nome:            string;
  nomeResponsavel: string | null;
  systemId:        string;
  registroNumero:  string | null;
  areaAtuacao:     string;
  tipoVinculo:     TipoVinculo;
  status:          "Ativo" | "Inativo";
  cidade:          string;
  estado:          string;
  localidade:      string;
  escolaVinculada: string;
  photoUrl:        string | null;
  resumoPublico:   string;
   createdAt:       string | null;  
   courseEndDate: string | null;
  links?:          { instagram?: string; site?: string };
};

const fmtDate = (d?: string | null): string => {
  if (!d) return '—';
  const part = String(d).slice(0, 10);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(part)) return '—';
  const [y, m, day] = part.split('-');
  const meses = ['janeiro','fevereiro','março','abril','maio','junho',
                  'julho','agosto','setembro','outubro','novembro','dezembro'];
  return `${parseInt(day)} de ${meses[parseInt(m) - 1]} de ${y}`;
};
const getTipoLabel = (r: ResultadoBusca): string => {
  if (r.tipo === "escola") return "Profissional / Escola";
  if (r.systemId?.startsWith("ALU-")) return "Aluno Certificado";
  if (r.systemId?.startsWith("PRF-")) return "Profissional Registrado";
  return "Profissional";
};

const getTipoCls = (r: ResultadoBusca): string => {
  if (r.tipo === "escola") return "bg-purple-100 text-purple-900 border-purple-200";
  if (r.systemId?.startsWith("ALU-")) return "bg-sky-100 text-sky-900 border-sky-200";
  if (r.systemId?.startsWith("PRF-")) return "bg-teal-100 text-teal-900 border-teal-200";
  return "bg-amber-100 text-amber-900 border-amber-200";
};

const VINCULO_CFG: Record<TipoVinculo, { bg: string; text: string; border: string }> = {
  "Revalidado":          { bg: "bg-amber-100",   text: "text-amber-900",   border: "border-amber-200"   },
  "Certificado":         { bg: "bg-blue-100",     text: "text-blue-900",    border: "border-blue-200"    },
  "Responsável Técnico": { bg: "bg-emerald-100",  text: "text-emerald-900", border: "border-emerald-200" },
};

const getInitials = (nome: string) =>
  nome.split(" ").slice(0, 2).map(n => n[0]).join("").toUpperCase();

const Avatar: React.FC<{ r: ResultadoBusca; size?: "sm" | "lg" }> = ({ r, size = "sm" }) => {
  const [imgError, setImgError] = useState(false);
  const dim = size === "lg" ? "w-20 h-20 text-2xl rounded-2xl" : "w-14 h-14 text-sm rounded-xl";
  const isEscola = r.tipo === "escola";
  const isAluno  = !isEscola && r.systemId?.startsWith("ALU-");
  const isPrf    = !isEscola && r.systemId?.startsWith("PRF-");
  const gradiente = isEscola
    ? "linear-gradient(135deg,#7c3aed,#6d28d9)"
    : isAluno
    ? "linear-gradient(135deg,#0ea5e9,#0369a1)"
    : isPrf
    ? "linear-gradient(135deg,#14b8a6,#0d9488)"
    : "linear-gradient(135deg,#d97706,#b45309)";

  if (r.photoUrl && !imgError) return (
    <div className={`${dim} overflow-hidden flex-shrink-0 border-2 border-amber-200 shadow-sm`}>
      <img
        src={r.photoUrl}
        alt={r.nome}
        className="w-full h-full object-cover"
        onError={() => setImgError(true)}
      />
    </div>
  );

  return (
    <div
      className={`${dim} flex items-center justify-center flex-shrink-0 font-black text-white shadow-sm`}
      style={{ background: gradiente }}
    >
      {getInitials(r.nome)}
    </div>
  );
};

const Chip: React.FC<{ label: string; cls: string }> = ({ label, cls }) => (
  <span className={`text-[10px] font-black px-2 py-0.5 rounded-full border whitespace-nowrap ${cls}`}>
    {label}
  </span>
);

const CardResultado: React.FC<{
  r: ResultadoBusca;
  onSelect: (r: ResultadoBusca) => void;
}> = ({ r, onSelect }) => {
  const vc       = VINCULO_CFG[r.tipoVinculo] ?? VINCULO_CFG["Certificado"];
  const isEscola = r.tipo === "escola";
  const isAluno  = !isEscola && r.systemId?.startsWith("ALU-");
  const isPrf    = !isEscola && r.systemId?.startsWith("PRF-");
  const barColor = isEscola
    ? "bg-gradient-to-r from-purple-400 to-violet-500"
    : isAluno
    ? "bg-gradient-to-r from-sky-400 to-blue-500"
    : isPrf
    ? "bg-gradient-to-r from-teal-400 to-emerald-500"
    : "bg-gradient-to-r from-amber-400 to-orange-500";

  return (
    <div className="bg-white rounded-2xl border border-gray-100
      shadow-[0_4px_24px_-10px_rgba(0,0,0,.18)]
      hover:shadow-[0_8px_32px_-10px_rgba(0,0,0,.26)]
      transition-all overflow-hidden group cursor-default">
      <div className={`h-1 w-full ${barColor}`}/>
      <div className="p-5 flex items-start gap-4">
        <Avatar r={r}/>
        <div className="flex-1 min-w-0">
          <div className="flex flex-wrap gap-1.5 mb-1.5">
            <Chip label={getTipoLabel(r)} cls={getTipoCls(r)}/>
            <Chip label={r.tipoVinculo} cls={`${vc.bg} ${vc.text} ${vc.border}`}/>
            {r.status === "Ativo" && (
              <span className="text-[10px] font-black px-2 py-0.5 rounded-full border
                bg-emerald-100 text-emerald-800 border-emerald-200 flex items-center gap-1">
                <CheckCircle className="w-3 h-3"/> Ativo
              </span>
            )}
          </div>
          <h3 className="font-black text-gray-900 text-[15px] leading-tight truncate">{r.nome}</h3>
          {isEscola && (
            <p className="text-xs text-gray-500 mt-0.5 truncate flex items-center gap-1">
              <User className="w-3 h-3"/>
              {r.nomeResponsavel
                ? r.nomeResponsavel
                : <span className="italic text-gray-400">Responsável não informado</span>
              }
            </p>
          )}
          {r.areaAtuacao && <p className="text-xs text-gray-500 mt-0.5 truncate">{r.areaAtuacao}</p>}
          <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-xs text-gray-400">
            <span className="flex items-center gap-1 font-mono">
              <Hash className="w-3 h-3 text-gray-300"/> {r.systemId}
            </span>
            {r.registroNumero && (
              <span className="flex items-center gap-1">
                <CreditCard className="w-3 h-3 text-amber-400"/> {r.registroNumero}
              </span>
            )}
            {r.localidade && (
              <span className="flex items-center gap-1">
                <MapPin className="w-3 h-3 text-amber-500"/> {r.localidade}
              </span>
            )}
          </div>
        </div>
        <button
          onClick={() => onSelect(r)}
          className="flex-shrink-0 flex items-center gap-1.5 px-4 py-2 rounded-xl
            bg-gray-900 text-white text-xs font-bold
            group-hover:bg-amber-700 transition-all">
          Ver <ArrowRight className="w-3.5 h-3.5"/>
        </button>
      </div>
    </div>
  );
};

// ─── Modal — usa `dados` (estado local) para sempre exibir info fresca do banco ─
const ModalPerfil: React.FC<{
  r: ResultadoBusca;
  onClose: () => void;
}> = ({ r, onClose }) => {
  const [dados,    setDados]    = useState<ResultadoBusca>(r);
  const [imgError, setImgError] = useState(false);

  // Rebusca os dados frescos ao abrir o modal
  useEffect(() => {
    fetch(`${API_BASE}/professionals/public-search?query=${encodeURIComponent(r.systemId)}&limit=1`)
      .then(res => res.json())
      .then(data => {
        const atualizado = data.results?.[0];
        if (atualizado) setDados(atualizado);
      })
      .catch(() => {}); // silencia erro — usa dado do card como fallback
  }, [r.systemId]);

  const vc       = VINCULO_CFG[dados.tipoVinculo] ?? VINCULO_CFG["Certificado"];
  const isEscola = dados.tipo === "escola";
  const isAluno  = !isEscola && dados.systemId?.startsWith("ALU-");
  const isPrf    = !isEscola && dados.systemId?.startsWith("PRF-");
  const qrUrl    = `${SITE_URL}/publicacoes/profissionais?q=${encodeURIComponent(dados.systemId)}`;
  const qrImg    = `https://api.qrserver.com/v1/create-qr-code/?size=120x120&margin=6&data=${encodeURIComponent(qrUrl)}`;
  const gradiente = isEscola
    ? "linear-gradient(135deg,#7c3aed,#6d28d9)"
    : isAluno
    ? "linear-gradient(135deg,#0ea5e9,#0369a1)"
    : isPrf
    ? "linear-gradient(135deg,#14b8a6,#0d9488)"
    : "linear-gradient(135deg,#d97706,#b45309)";

  return (
    <div className="fixed inset-0 z-50">
      <div className="absolute inset-0 bg-black/65 backdrop-blur-sm" onClick={onClose}/>
      <div className="absolute inset-0 flex items-end sm:items-center justify-center p-4 overflow-y-auto">
        <div className="w-full max-w-lg bg-white rounded-3xl border border-amber-100 shadow-2xl overflow-hidden my-4">

          {/* Barra topo */}
          <div className={`h-1.5 w-full ${isEscola
            ? "bg-gradient-to-r from-purple-400 to-violet-500"
            : isAluno
            ? "bg-gradient-to-r from-sky-400 to-blue-500"
            : isPrf
            ? "bg-gradient-to-r from-teal-400 to-emerald-500"
            : "bg-gradient-to-r from-amber-400 to-orange-500"}`}/>

          {/* Cabeçalho */}
          <div className="p-6 border-b border-gray-100">
            <div className="flex items-start gap-4">
              <div className="w-20 h-20 rounded-2xl overflow-hidden flex-shrink-0 border-2 border-amber-200 shadow-md">
                {dados.photoUrl && !imgError ? (
                  <img
                    src={dados.photoUrl}
                    alt={dados.nome}
                    className="w-full h-full object-cover"
                    onError={() => setImgError(true)}
                  />
                ) : (
                  <div
                    className="w-full h-full flex items-center justify-center font-black text-white text-2xl"
                    style={{ background: gradiente }}
                  >
                    {getInitials(dados.nome)}
                  </div>
                )}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex flex-wrap gap-1.5 mb-2">
                  <Chip label={getTipoLabel(dados)} cls={getTipoCls(dados)}/>
                  <Chip label={dados.tipoVinculo} cls={`${vc.bg} ${vc.text} ${vc.border}`}/>
                  {dados.status === "Ativo" && (
                    <Chip label="✓ Ativo" cls="bg-emerald-100 text-emerald-800 border-emerald-200"/>
                  )}
                </div>
                <h3 className="text-xl font-black text-gray-900 leading-tight">{dados.nome}</h3>
                {isEscola && (
                  <p className="text-sm text-gray-600 mt-1 flex items-center gap-1.5">
                    <User className="w-3.5 h-3.5 text-amber-500"/>
                    {dados.nomeResponsavel ?? <span className="italic text-gray-400">Não informado</span>}
                  </p>
                )}
                {dados.areaAtuacao && <p className="text-sm text-gray-500 mt-0.5">{dados.areaAtuacao}</p>}
              </div>
              <button onClick={onClose}
                className="w-9 h-9 rounded-xl border border-gray-200 bg-white hover:bg-gray-50
                  transition flex items-center justify-center flex-shrink-0">
                <X className="w-4 h-4 text-gray-600"/>
              </button>
            </div>
          </div>

          {/* Corpo */}
          <div className="p-6 space-y-4">
            <div className="grid grid-cols-2 gap-3">

              <div className="rounded-xl border border-gray-100 bg-gray-50 p-3">
                <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-0.5 flex items-center gap-1">
                  <Hash className="w-3 h-3"/> ID do Sistema
                </p>
                <p className="text-sm font-bold text-gray-800 font-mono">{dados.systemId}</p>
              </div>

              <div className="rounded-xl border border-gray-100 bg-gray-50 p-3">
                <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-0.5 flex items-center gap-1">
                  <CreditCard className="w-3 h-3"/> Registro FEPCSSP
                </p>
                <p className="text-sm font-bold text-gray-800">
                  {dados.registroNumero ?? <span className="text-gray-400 font-normal text-xs">Em cadastramento</span>}
                </p>
              </div>


{/* ← NOVO CARD AQUI */}
{dados.courseEndDate && (
  <div className="rounded-xl border border-gray-100 bg-gray-50 p-3">
    <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-0.5 flex items-center gap-1">
      <Calendar className="w-3 h-3"/> Profissional desde
    </p>
    <p className="text-sm font-bold text-gray-800">
      {fmtDate(dados.courseEndDate)}
    </p>
  </div>
)}
              {dados.localidade && (
                <div className="rounded-xl border border-gray-100 bg-gray-50 p-3">
                  <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-0.5 flex items-center gap-1">
                    <MapPin className="w-3 h-3"/> Localidade
                  </p>
                  <p className="text-sm font-bold text-gray-800">{dados.localidade}</p>
                </div>
              )}

              {isEscola && (
                <div className="rounded-xl border border-gray-100 bg-gray-50 p-3">
                  <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-0.5 flex items-center gap-1">
                    <User className="w-3 h-3"/> Responsável
                  </p>
                  <p className="text-sm font-bold text-gray-800">
                    {dados.nomeResponsavel ?? <span className="text-gray-400 font-normal text-xs italic">Não informado</span>}
                  </p>
                </div>
              )}

              {dados.areaAtuacao && (
                <div className="rounded-xl border border-gray-100 bg-gray-50 p-3">
                  <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-0.5">
                    {dados.tipo === 'escola' ? '🏫 Tipo'
                      : dados.systemId?.startsWith('ALU-') ? '🎓 Curso'
                      : dados.systemId?.startsWith('PRF-') ? '💼 Função'
                      : '💼 Especialidade'}
                  </p>
                  <p className="text-sm font-bold text-gray-800">{dados.areaAtuacao}</p>
                </div>
              )}

              {dados.escolaVinculada && (
                <div className="rounded-xl border border-gray-100 bg-gray-50 p-3 col-span-2">
                  <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-0.5 flex items-center gap-1">
                    <Building2 className="w-3 h-3"/> {dados.systemId?.startsWith('ALU-') ? 'Escola Formadora' : 'Escola Vinculada'}
                  </p>
                  <p className="text-sm font-bold text-gray-800">{dados.escolaVinculada}</p>
                </div>
              )}

              {/* Badge tipo */}
              <div className="col-span-2 rounded-xl border border-gray-100 bg-gray-50 p-3 flex items-center gap-3">
                <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${
                  dados.tipo === 'escola' ? 'bg-purple-100'
                    : dados.systemId?.startsWith('ALU-') ? 'bg-sky-100'
                    : dados.systemId?.startsWith('PRF-') ? 'bg-teal-100'
                    : 'bg-amber-100'
                }`}>
                  {dados.tipo === 'escola'
                    ? <Building2 className="w-4 h-4 text-purple-600"/>
                    : dados.systemId?.startsWith('ALU-')
                    ? <Award className="w-4 h-4 text-sky-600"/>
                    : dados.systemId?.startsWith('PRF-')
                    ? <CheckCircle className="w-4 h-4 text-teal-600"/>
                    : <CheckCircle className="w-4 h-4 text-amber-600"/>}
                </div>
                <div>
                  <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider">Tipo de Cadastro</p>
                  <p className="text-sm font-bold text-gray-800">
                    {dados.tipo === 'escola'
                      ? 'Profissional / Escola Credenciada'
                      : dados.systemId?.startsWith('ALU-')
                      ? 'Aluno Certificado pela Federação'
                      : dados.systemId?.startsWith('PRF-')
                      ? 'Profissional Registrado na Federação'
                      : 'Profissional Registrado'}
                  </p>
                </div>
              </div>
            </div>

            {dados.resumoPublico && (
              <div className="rounded-xl border border-amber-100 bg-amber-50/50 p-4 text-sm text-gray-700 leading-relaxed">
                {dados.resumoPublico}
              </div>
            )}

            <div className="rounded-xl border border-gray-100 p-4 flex items-center gap-4">
              <div className="w-20 h-20 flex-shrink-0 rounded-xl overflow-hidden border border-gray-200 p-1 bg-white shadow-sm">
                <img src={qrImg} alt="QR Code" className="w-full h-full object-contain"/>
              </div>
              <div className="min-w-0">
                <p className="text-sm font-black text-gray-900 mb-0.5">QR Code de verificação</p>
                <p className="text-xs text-gray-500 leading-relaxed">
                  Escaneie para confirmar a autenticidade deste registro na Federação.
                </p>
                <p className="text-[10px] font-mono text-amber-600 mt-1 bg-amber-50 px-2 py-0.5 rounded-lg inline-block">
                  {dados.systemId}
                </p>
              </div>
            </div>

            {(dados.links?.site || dados.links?.instagram) && (
              <div className="flex gap-2">
                {dados.links?.site && (
                  <a href={dados.links.site} target="_blank" rel="noopener noreferrer"
                    className="flex items-center gap-1.5 px-3 py-2 rounded-xl border border-gray-200 text-xs font-semibold text-gray-600 hover:bg-gray-50 transition">
                    <ExternalLink className="w-3.5 h-3.5"/> Site
                  </a>
                )}
                {dados.links?.instagram && (
                  <a href={`https://instagram.com/${dados.links.instagram}`} target="_blank" rel="noopener noreferrer"
                    className="flex items-center gap-1.5 px-3 py-2 rounded-xl border border-gray-200 text-xs font-semibold text-gray-600 hover:bg-gray-50 transition">
                    <ExternalLink className="w-3.5 h-3.5"/> Instagram
                  </a>
                )}
              </div>
            )}

            <div className="flex gap-3 pt-1">
              <Link to="/servicos/revalida"
                className="flex-1 inline-flex items-center justify-center gap-2 px-5 py-3
                  rounded-xl bg-gray-900 text-white text-sm font-bold hover:shadow-lg transition">
                Regularizar <ArrowRight className="w-4 h-4"/>
              </Link>
              <button onClick={onClose}
                className="px-5 py-3 rounded-xl border border-gray-200 text-sm font-bold text-gray-700 hover:bg-gray-50 transition">
                Fechar
              </button>
            </div>
          </div>

          {/* Rodapé */}
          <div className="px-6 py-4 bg-gray-950 flex items-center justify-between">
            <div className="flex items-center gap-2 text-xs font-semibold text-white/50">
              <ShieldCheck className="w-3.5 h-3.5 text-amber-400"/>
              Consulta pública institucional
            </div>
            <div className="flex items-center gap-1.5 text-xs font-black text-amber-300">
              <BadgeCheck className="w-3.5 h-3.5"/> FEPCSSP-BR
            </div>
          </div>

        </div>
      </div>
    </div>
  );
};


// ─── Página principal ─────────────────────────────────────────────────────────
const Profissionais: React.FC = () => {
  const [query,        setQuery]        = useState("");
  const [buscou,       setBuscou]       = useState(false);
  const [loading,      setLoading]      = useState(false);
  const [erroApi,      setErroApi]      = useState<string | null>(null);
  const [erroFormato,  setErroFormato]  = useState(false);
  const [mostrarDicas, setMostrarDicas] = useState(false);
  const [resultados,   setResultados]   = useState<ResultadoBusca[]>([]);
  const [selecionado,  setSelecionado]  = useState<ResultadoBusca | null>(null);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const q = params.get('q');
    if (q) { setQuery(q); setTimeout(() => buscar(), 100); }
  }, []);

  const buscar = async () => {
    const q = query.trim();
    setBuscou(true);
    setErroApi(null);
    setErroFormato(false);
    setResultados([]);

    if (q.length < 3) return;

    if (!isQueryValida(q)) {
      setErroFormato(true);
      return;
    }

    const searchTerm = sanitizeQuery(q);

    setLoading(true);
    try {
      const res = await fetch(
        `${API_BASE}/professionals/public-search?query=${encodeURIComponent(searchTerm)}&limit=8`,
      );
      if (!res.ok) throw new Error((await res.json().catch(() => ({}))).message ?? `Erro ${res.status}`);
      const data = await res.json();
      setResultados(data.results ?? []);
    } catch (err: any) {
      setErroApi(err?.message ?? "Falha ao consultar. Tente novamente.");
    } finally { setLoading(false); }
  };

  const onSubmit: React.FormEventHandler<HTMLFormElement> = (e) => { e.preventDefault(); buscar(); };
  const limpar = () => {
    setQuery("");
    setBuscou(false);
    setErroApi(null);
    setErroFormato(false);
    setResultados([]);
    setSelecionado(null);
  };

  const profissionais = resultados.filter(r => r.tipo === "profissional");
  const escolas       = resultados.filter(r => r.tipo === "escola");

  return (
    <div className="min-h-screen bg-gradient-to-br from-amber-50 via-white to-orange-50/30">

      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-gray-950 via-amber-950 to-gray-950"/>
        <div className="absolute inset-0 opacity-15">
          <div className="absolute -top-24 -left-24 w-[520px] h-[520px] bg-amber-500 rounded-full blur-3xl"/>
          <div className="absolute -bottom-24 -right-24 w-[520px] h-[520px] bg-orange-500 rounded-full blur-3xl"/>
        </div>
        <div className="container mx-auto px-6 relative z-10 py-16 md:py-20 text-center">
          <div className="inline-flex items-center gap-3 px-6 py-3 bg-white/10 backdrop-blur-md rounded-full border border-white/20 mb-8">
            <ShieldCheck className="w-5 h-5 text-amber-300"/>
            <span className="text-sm font-bold text-amber-100 uppercase tracking-wider">Verificação de Autenticidade</span>
          </div>
          <h1 className="text-4xl md:text-5xl font-black text-white mb-5">Consulta de Autenticidade</h1>
          <p className="text-base text-white/80 max-w-2xl mx-auto mb-10">
            Digite o ID do profissional (ALU- / FIL- / PRF-) ou o número de registro FEPCSSP para verificar a autenticidade do cadastro.
          </p>
          <div className="flex flex-col sm:flex-row justify-center gap-3">
            <a href="#pesquisa"
              className="inline-flex items-center justify-center gap-2 px-7 py-3.5 bg-gradient-to-r from-amber-600 to-orange-600 text-white font-bold rounded-xl shadow-lg hover:scale-[1.02] transition-all">
              Consultar agora <ArrowRight className="w-5 h-5"/>
            </a>
            <Link to="/servicos/revalida"
              className="inline-flex items-center justify-center gap-2 px-7 py-3.5 bg-white/10 border border-white/20 text-white font-bold rounded-xl hover:bg-white/15 transition-all">
              Revalida Pro <ArrowRight className="w-5 h-5"/>
            </Link>
          </div>
        </div>
        <div className="h-px w-full bg-gradient-to-r from-transparent via-white/10 to-transparent"/>
      </section>

   {/* PESQUISA */}
<section id="pesquisa" className="py-14 md:py-20">
  <div className="container mx-auto px-6">
    <div className="max-w-3xl mx-auto">

      <div className="bg-white rounded-3xl border border-amber-100/70 shadow-[0_25px_60px_-35px_rgba(0,0,0,.35)] overflow-hidden">

        <div className="bg-gradient-to-br from-gray-950 to-gray-900 p-7 text-center">
          {/* LOGO substituindo o ícone de escudo */}
          <div className="mx-auto mb-4 flex items-center justify-center">
            <img
              src="/imagem/Bras.png"
              alt="Logo"
              className="h-20 w-auto object-contain drop-shadow-[0_2px_8px_rgba(0,0,0,0.5)]"
            />
          </div>

          <h2 className="text-xl font-black text-white">Verificar autenticidade</h2>
          <p className="mt-2 text-sm text-white/55">
            Busque pelo ID do cadastro (FIL- / ALU- / PRF-) ou pelo número de registro FEPCSSP
          </p>
        </div>

              <div className="p-6 md:p-8">
                <form onSubmit={onSubmit}>
                  <div className="relative">
                    <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400"/>
                    <input
                      value={query}
                      onChange={e => { setQuery(e.target.value); setErroFormato(false); }}
                      placeholder="Ex.: ALU-2026-XXXX  •  FIL-2026-XXXX  •  PRF-2026-XXXX  •  001234"
                      className="w-full pl-12 pr-4 py-4 border-2 border-gray-200 rounded-2xl
                        focus:ring-2 focus:ring-amber-500 focus:border-transparent transition bg-white
                        text-gray-800 placeholder:text-gray-400 text-sm"
                    />
                  </div>

                  <div className="mt-3 flex gap-3">
                    <button type="submit" disabled={loading}
                      className="flex-1 inline-flex items-center justify-center gap-2 px-6 py-3.5
                        rounded-2xl font-black transition disabled:opacity-60 text-white"
                      style={{ background: "linear-gradient(135deg,#1f2937,#111827)" }}>
                      {loading
                        ? <><Loader2 className="w-5 h-5 animate-spin"/> Consultando…</>
                        : <><Search className="w-5 h-5"/> Consultar</>}
                    </button>
                    {buscou && (
                      <button type="button" onClick={limpar}
                        className="px-5 py-3.5 rounded-2xl border border-gray-200 text-gray-600 font-semibold hover:bg-gray-50 transition flex items-center gap-2 text-sm">
                        <X className="w-4 h-4"/> Limpar
                      </button>
                    )}
                  </div>

                  <button type="button" onClick={() => setMostrarDicas(v => !v)}
                    className="mt-4 w-full inline-flex items-center justify-center gap-2 text-xs font-semibold text-gray-400 hover:text-gray-600 transition">
                    Como pesquisar <ChevronDown className={`w-3.5 h-3.5 transition-transform ${mostrarDicas ? "rotate-180" : ""}`}/>
                  </button>

                  {mostrarDicas && (
                    <div className="mt-3 rounded-2xl border border-gray-100 bg-gray-50 p-4 text-xs text-gray-600 space-y-2">
                      {[
                        { icon: "🎓", text: "ID de aluno: \"ALU-2026-ABCDEF\" (o ID da carteirinha)" },
                        { icon: "🏫", text: "ID de escola / filiado: \"FIL-2026-AAJJYW\"" },
                        { icon: "💼", text: "ID de profissional: \"PRF-2026-ABCDEF\"" },
                        { icon: "🏅", text: "Registro FEPCSSP: 6 dígitos — ex.: \"001239\"" },
                      ].map(({ icon, text }) => (
                        <p key={text} className="flex items-start gap-2">
                          <span className="flex-shrink-0">{icon}</span> {text}
                        </p>
                      ))}
                      <p className="flex items-start gap-2 pt-1 border-t border-gray-200 text-gray-400">
                        <span className="flex-shrink-0">🔒</span>
                        A busca por nome não é permitida para proteger a privacidade dos profissionais.
                      </p>
                    </div>
                  )}

                  {erroFormato && (
                    <div className="mt-4 rounded-2xl border border-amber-300 bg-amber-50 p-4 text-sm text-amber-900">
                      <p className="font-black mb-1">Formato de busca inválido</p>
                      <p className="text-amber-800">
                        A consulta aceita apenas <strong>IDs</strong> (ALU- / FIL- / PRF-) ou o
                        número de <strong>registro FEPCSSP</strong> com exatamente 6 dígitos.
                        Verifique o código no seu certificado ou carteirinha.
                      </p>
                    </div>
                  )}

                  {erroApi && (
                    <div className="mt-4 rounded-2xl border border-red-200 bg-red-50 p-4 text-sm text-red-800">
                      {erroApi}
                    </div>
                  )}

                  {buscou && query.trim().length > 0 && query.trim().length < 3 && (
                    <div className="mt-4 rounded-2xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900">
                      Digite pelo menos <strong>3 caracteres</strong>.
                    </div>
                  )}

                  {buscou && !loading && !erroApi && !erroFormato && resultados.length === 0 && query.trim().length >= 3 && (
                    <div className="mt-6 rounded-2xl border border-gray-200 bg-gray-50 p-5">
                      <p className="font-black text-gray-900 mb-1">Nenhum registro encontrado</p>
                      <p className="text-sm text-gray-500 mb-4">Verifique o ID ou número e tente novamente.</p>
                      <div className="flex flex-wrap gap-2">
                        <Link to="/servicos/revalida"
                          className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-gray-900 text-white text-sm font-bold hover:shadow-lg transition">
                          Revalida Pro <ArrowRight className="w-3.5 h-3.5"/>
                        </Link>
                        <Link to="/fale-conosco"
                          className="inline-flex items-center gap-2 px-4 py-2 rounded-xl border border-gray-200 bg-white text-sm font-bold text-gray-800 hover:bg-gray-50 transition">
                          Falar com a Federação
                        </Link>
                      </div>
                    </div>
                  )}
                </form>
              </div>
            </div>

            {resultados.length > 0 && (
              <div className="mt-10 space-y-8">
                {profissionais.length > 0 && (
                  <div>
                    <div className="flex items-center gap-3 mb-4">
                      <div className="w-7 h-7 rounded-lg bg-amber-100 flex items-center justify-center">
                        <Award className="w-4 h-4 text-amber-700"/>
                      </div>
                      <p className="font-black text-gray-900">
                        Profissionais <span className="text-amber-600 font-bold">({profissionais.length})</span>
                      </p>
                    </div>
                    <div className="space-y-3">
                      {profissionais.map(r => <CardResultado key={r.id} r={r} onSelect={setSelecionado}/>)}
                    </div>
                  </div>
                )}
                {escolas.length > 0 && (
                  <div>
                    <div className="flex items-center gap-3 mb-4">
                      <div className="w-7 h-7 rounded-lg bg-purple-100 flex items-center justify-center">
                        <Building2 className="w-4 h-4 text-purple-700"/>
                      </div>
                      <p className="font-black text-gray-900">
                        Escolas / Estúdios <span className="text-purple-600 font-bold">({escolas.length})</span>
                      </p>
                    </div>
                    <div className="space-y-3">
                      {escolas.map(r => <CardResultado key={r.id} r={r} onSelect={setSelecionado}/>)}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </section>

      {selecionado && <ModalPerfil r={selecionado} onClose={() => setSelecionado(null)}/>}

      {/* CTA final */}
      <section className="py-16">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto rounded-3xl bg-gradient-to-br from-gray-900 to-gray-950 text-white p-8 md:p-10 overflow-hidden relative">
            <div className="absolute -top-20 -right-20 w-72 h-72 bg-amber-500/15 rounded-full blur-3xl"/>
            <div className="absolute -bottom-20 -left-20 w-72 h-72 bg-orange-500/15 rounded-full blur-3xl"/>
            <div className="relative z-10 flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
              <div>
                <p className="text-[10px] font-bold tracking-[0.2em] text-amber-400/70 uppercase mb-2">Regularização profissional</p>
                <h3 className="text-2xl font-black mb-2">Precisa regularizar?</h3>
                <p className="text-white/70 max-w-xl text-sm leading-relaxed">
                  Se você ainda não possui diploma reconhecido ou registro ativo, inicie o processo pelo Revalida Pro.
                </p>
              </div>
              <Link to="/servicos/revalida"
                className="inline-flex items-center gap-2 px-7 py-3.5 rounded-xl font-bold text-gray-900 hover:shadow-xl transition-all whitespace-nowrap flex-shrink-0"
                style={{ background: "linear-gradient(135deg,#f4d03f,#d4af37)" }}>
                Iniciar Revalida Pro <ArrowRight className="w-5 h-5"/>
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Profissionais;